/**
 * Jump level — how loudly a just-arrived notification interrupts the player.
 * Ships on the `notification:created` realtime cue payload as the effective,
 * post-cooldown value the backend already resolved (per-user rate limiting +
 * per-type ceiling live server-side; the client just obeys).
 *
 *  - `BELL`   — ring the bell + refresh queries only. No banner, no pop-up.
 *  - `BANNER` — ring + show the pop-in banner (today's default behavior).
 *  - `POPUP`  — ring + auto-open the detail pop-up for the notification.
 *
 * NOTE (Orval reconciliation): this is a hand-typed mirror of a field on the
 * realtime cue that the generated client / `predictions-realtime-contracts`
 * will later formalize. Kept localized here so regen just deletes this file and
 * repoints the import. See `useNotificationsRealtime`.
 */
export type JumpLevel = 'BELL' | 'BANNER' | 'POPUP';

/** Today's behavior — used when the cue omits or mis-types `jumpLevel`, so nothing regresses. */
export const DEFAULT_JUMP_LEVEL: JumpLevel = 'BANNER';

const JUMP_LEVELS: readonly JumpLevel[] = ['BELL', 'BANNER', 'POPUP'];

/**
 * Pull the effective jump level off a loosely-typed cue payload, defaulting to
 * {@link DEFAULT_JUMP_LEVEL} for a missing / unknown value.
 */
export function cueJumpLevel(payload: unknown): JumpLevel {
  if (payload !== null && typeof payload === 'object' && 'jumpLevel' in payload) {
    const level = (payload as { jumpLevel: unknown }).jumpLevel;
    if (typeof level === 'string' && (JUMP_LEVELS as readonly string[]).includes(level)) {
      return level as JumpLevel;
    }
  }
  return DEFAULT_JUMP_LEVEL;
}
